// @flow

import React, {type Node} from 'react';

import type {StoredResponseType} from 'app/utils/redux_v2';
import {deepDiffer} from 'app/utils/react-utils';

export type InfiniteListFetcherProps<T> = {|
  fetchParams?: {[string]: any},
  pageParam?: string,
  children: ({
    allResults: Array<T>,
    resultsFingerprint: number,
    loadNext: () => void,
    hasNext: boolean,
    loading: boolean,
    loadingLast: boolean,
    totalCount: number,
    refetchData: () => Promise<any>,
  }) => Node,
  skipInitialFetch?: boolean,
  hideLoadingIndicator?: boolean,
  onFetch?: (Array<StoredResponseType<T>>) => any,
|};

type Props<T> = {|
  ...InfiniteListFetcherProps<T>,
  DataFetcher: any,
|};

type State = {|
  paramsArray: Array<{[string]: any}>,
|};

export class InfiniteListFetcher<T: {id: number}> extends React.Component<
  Props<T>,
  State
> {
  constructor(props: Props<T>) {
    super(props);
    const params = this.buildParamsArray();
    this.state = {
      paramsArray: params
    };
  }

  buildParamsArray = () => (
      [{...(this.props.fetchParams || {}), [this.props.pageParam || 'page']: 1}]
  )

  componentDidUpdate(prevProps: Props<T>) {
    const {fetchParams} = this.props;

    if (deepDiffer(prevProps.fetchParams, fetchParams)) {
      this.setState({
        paramsArray: this.buildParamsArray(),
      });
    }
  }

  loadNext = () => {
    const {paramsArray} = this.state;
    const lastPageParams = paramsArray.slice(-1)[0];
    const nextPage = lastPageParams[this.props.pageParam || 'page'] + 1;
    this.setState({paramsArray: [...this.state.paramsArray, {...lastPageParams, [this.props.pageParam || 'page']: nextPage}] });
  };

  mergeAllResults(responses: Array<StoredResponseType<T>>): [Array<T>, number] {
    let fingerprint = 0;

    const results = responses.reduce((results, response) => {
      fingerprint += response.results.reduce((a, b) => a + b.id, 0);
      return results.concat(response.results);
    }, []);

    return [results, fingerprint];
  }

  render() {
    const {
      children,
      onFetch,
      skipInitialFetch,
      hideLoadingIndicator,
      DataFetcher,
    } = this.props;
    const {paramsArray} = this.state;

    return (
      <DataFetcher
        paramsArray={paramsArray}
        skipInitialFetch={skipInitialFetch}
        hideLoadingIndicator={hideLoadingIndicator}
        onFetch={onFetch}
      >
        {(responses, refetchData) => {
          const [allResults, resultsFingerprint] =
            this.mergeAllResults(responses);
          const firstResponse = responses[0];
          const lastResponse = responses[responses.length - 1];

          return children({
            allResults,
            resultsFingerprint,
            hasNext: lastResponse.next != null,
            loadNext: this.loadNext,
            loading: firstResponse.loading,
            loadingLast: lastResponse.loading,
            totalCount: firstResponse.count,
            refetchData,
          });
        }}
      </DataFetcher>
    );
  }
}
